import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Search, HelpCircle, FileText, Calendar, Car, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const HowToCheckPCPMisSoldPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                How to Check if Your PCP was Mis-sold
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Signs that your car finance may have been affected by Discretionary Commission Arrangements
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                If you took out PCP (Personal Contract Purchase) or HP (Hire Purchase) car finance 
                between <strong>April 2007 and January 2021</strong>, your agreement may have been affected by 
                Discretionary Commission Arrangements (DCAs). Here's how to assess whether you 
                may have a claim.
              </p>
            </div>
          </div>
        </section>

        {/* Key Questions Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Key Questions to Consider
                </h2>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">1</span>
                    When Did You Take Out the Finance?
                  </h3>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        The FCA's investigation focuses on agreements entered into between <strong className="text-foreground">April 2007 and 
                        January 2021</strong> (when DCAs were banned). If your finance agreement falls within this 
                        period, it may be worth investigating further.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">2</span>
                    Were You Told About Commission?
                  </h3>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4">
                        One of the most significant indicators of potential mis-selling is whether you were 
                        told that the dealer received commission from the lender. Consider:
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Did the dealer mention commission at all?</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Were you told how much commission the dealer would receive?</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Were you informed that the dealer could influence your interest rate?</span>
                        </li>
                      </ul>
                      <Card className="bg-destructive/5 border-destructive mt-4">
                        <CardContent className="p-4">
                          <p className="text-foreground font-medium">
                            If you were not told about commission arrangements, this could indicate your finance was mis-sold.
                          </p>
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">3</span>
                    Did You Feel You Had a Choice?
                  </h3>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4">
                        You should have been given the opportunity to:
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Compare finance options from different providers</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Consider whether you needed finance at all</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Take time to think about the decision</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Understand the full cost of the finance</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">4</span>
                    Was the Interest Rate Explained?
                  </h3>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground mb-4">
                        The APR (Annual Percentage Rate) should have been clearly explained. Consider:
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Were you told the interest rate before signing?</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Did you understand how much interest you would pay in total?</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">Were you given the opportunity to negotiate the rate?</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Documents to Check */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Documents to Check
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                If you still have your finance documents, look for information about:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "The APR (Annual Percentage Rate) on your agreement",
                  "Any mention of commission or brokerage fees",
                  "The total amount payable over the agreement",
                  "The name of the lender who provided the finance"
                ].map((item, idx) => (
                  <Card key={idx} className="border-border">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Search className="w-5 h-5 text-gold flex-shrink-0" />
                      <p className="text-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-foreground font-medium mb-2">Don't Have Your Documents?</p>
                      <p className="text-muted-foreground">
                        If you no longer have your finance paperwork, don't worry. <strong className="text-foreground">Blue Lion Law can help 
                        obtain the necessary information</strong> from the relevant parties as part of the claims process.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Types of Finance */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <Car className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  Types of Finance That May Be Affected
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                DCAs were used across various types of motor finance, including:
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { name: "PCP (Personal Contract Purchase)", desc: "The most common form of car finance" },
                  { name: "HP (Hire Purchase)", desc: "Pay off the full value over the term" },
                  { name: "Conditional Sale Agreements", desc: "Similar to HP with different terms" },
                  { name: "Personal Loans", desc: "Some loans used to purchase vehicles" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-primary-foreground/10 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-primary-foreground mb-2">{item.name}</h3>
                    <p className="text-primary-foreground/80 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What Information Do You Need */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                What Information Do You Need?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                To assess a potential claim, it's helpful (but not essential) to know:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "When you took out the finance (approximate date)",
                  "The name of the dealership",
                  "The type of vehicle purchased",
                  "The type of finance (PCP, HP, etc.)",
                  "The lender (if known)"
                ].map((item, idx) => (
                  <Card key={idx} className="border-border">
                    <CardContent className="p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <p className="text-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gold/5 border-gold">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    Even if you don't have all this information, <strong>it may still be possible to investigate 
                    your claim</strong>. We can work with what you remember to piece together the details.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                What Happens Next?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                If you believe your PCP or HP finance may have been affected by DCAs:
              </p>

              <div className="space-y-4">
                {[
                  { step: "1", title: "Initial Assessment", desc: "Blue Lion Law will review your circumstances to determine if you may have a claim" },
                  { step: "2", title: "Gathering Information", desc: "We can request relevant documents and information from the lender and dealer" },
                  { step: "3", title: "Claim Submission", desc: "If you have a valid claim, we will handle the process on your behalf" },
                  { step: "4", title: "Resolution", desc: "We will work to secure compensation if you are entitled to it" }
                ].map((item, idx) => (
                  <Card key={idx} className="border-l-4 border-l-accent">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 text-white font-bold">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Important Note */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <Card className="bg-gold/5 border-gold">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-foreground font-medium mb-2">Important Note</p>
                      <p className="text-muted-foreground">
                        Not everyone who took out car finance during this period will have a valid claim. 
                        Eligibility depends on individual circumstances, including whether DCAs were 
                        actually used in your specific agreement. <strong className="text-foreground">Blue Lion Law assesses each case on its 
                        merits</strong> and will provide honest advice about your prospects.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Ready to Check Your Claim?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                It takes a few minutes to start your claim.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="/pcp-car-finance-claims">
                    Start Your Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="tel:02081530033">
                    Call 0208 153 0033
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/pcp/how-pcp-is-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How PCP is Mis-sold</span>
                </a>
                <a href="/pcp/pcp-claims-faq" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Claims FAQ</span>
                </a>
                <a href="/pcp-car-finance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Car Finance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowToCheckPCPMisSoldPage;
